import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ★ propsとして mode を受け取る
function FormGatekeeper({ mode }) {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [title, setTitle] = useState('');
  
  // ★ 追加: キャンセル処理用のステート
  const [uuid, setUuid] = useState(null); 
  const [isCanceling, setIsCanceling] = useState(false); 
  const [cancelFinished, setCancelFinished] = useState(false);

  const location = useLocation();

  // GASウェブアプリURL
  const gasUrl = "https://script.google.com/macros/s/AKfycbxV78M9f2XuypgAEfJ3An-chZD1b_FmDONcUuuP9gCCMdY8hwCrGBatowwBVEXxIevS6A/exec"; 

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userID = searchParams.get('userID');
    const userName = searchParams.get('userName');
    const params = searchParams.get('params') || 'slidedigital';
    
    // URLパラメータから editUrl を取得
    const editUrl = searchParams.get('editUrl');

    if (!userID) {
      setError("ユーザーIDが確認できません。公式LINEより再度お申し込みください。");
      setIsLoading(false);
      return;
    }

    // GASに送信するデータ
    const payload = {
      action: "initLiffApp", 
      userID: userID,
      userName: userName,
      params: params,
      mode: mode || 'new',      // 'new' または 'edit'
      isGatekeeper: true // ★ これを追加することで、GASが「次は本番フォームURLを返せばいいんだな」と判断できます
    };

    fetch(gasUrl, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain", 
      },
      body: JSON.stringify(payload)
    })
    .then(response => response.json())
    .then(data => {
      setIsLoading(false);
      if (data.error) {
        // GAS側で「確定済み」や「進行中」と判定された場合はここでエラー表示
        setError(data.error);
        setTitle(data.title);
        if (data.uuid) setUuid(data.uuid); // ★ 追加: 進行中のUUIDを保存しておく
      } else {
        // 編集モードで editUrl がパラメータに存在すれば、そのURLを開く
        if (mode === 'edit' && editUrl) {
          window.location.href = editUrl;
        } else if (data.formUrl) {
          // 新規モード、または editUrl が渡されていない場合は通常のフォームURLを開く
          window.location.href = data.formUrl;
        } else {
          setError("システムエラーが発生しました。");
        }
      }
    })
    .catch(err => {
      console.error("Fetch error:", err);
      setIsLoading(false);
      setError("サーバーとの通信に失敗しました。時間をおいて再度お試しください。");
    });
  }, [location, gasUrl, mode]);

  // ==========================================
  // ★ 追加: キャンセルボタンを押した時の処理
  // ==========================================
  const handleCancel = async () => {
    const confirmCancel = window.confirm("進行中の案件をキャンセルして、新しくお申し込みできるようにしますか？\n※一度キャンセルすると元に戻せません。");
    if (!confirmCancel) return;

    setIsCanceling(true);
    const searchParams = new URLSearchParams(location.search);

    try {
      // Reconfirm画面と同じく、GASのキャンセル処理アクションを呼び出す
      const payload = {
        action: "handleCancelRequest", 
        uuid: uuid,
        userID: searchParams.get('userID'),
        userName: searchParams.get('userName'),
        params: searchParams.get('params') || 'slidedigital'
      };

      const res = await fetch(gasUrl, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      
      if (result.error) throw new Error(result.error);
      
      // キャンセル成功画面へ切り替え
      setCancelFinished(true);
    } catch (err) {
      alert("キャンセル処理に失敗しました: " + err.message);
    } finally {
      setIsCanceling(false);
    }
  };


  // ==========================================
  // レスポンシブ＆中央揃え用のスタイル
  // ==========================================
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',  
    width: '100%',
    fontFamily: '"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    padding: '0 20px'    
  };

  const textStyle = {
    fontSize: '18px',
    color: '#333',
    fontWeight: 'bold',
    margin: '0 0 10px 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%' 
  };

  const errorTitleStyle = {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#1a73e8', 
    marginBottom: '20px'
  };

  const errorTextStyle = {
    fontSize: '15px',
    color: '#333',
    lineHeight: '1.6',
    marginBottom: '30px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap' 
  };

  const contactStyle = {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.5',
    paddingTop: '20px',
    borderTop: '1px solid #ddd', 
    width: '100%',
    maxWidth: '300px'
  };

  // ★ 追加: キャンセルボタン用のスタイル
  const cancelBtnStyle = {
    backgroundColor: '#d93025',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 20px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '300px',
    marginBottom: '25px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };
  // ==========================================

  // 通信中・キャンセル処理中の表示
  if (isLoading || isCanceling) {
    return (
      <div style={containerStyle}>
        <p style={textStyle}>
          {isCanceling ? "キャンセル処理中です..." : "お申し込みフォームへ移動しています..."}
        </p>
        <p style={{ fontSize: '13px', color: '#999' }}>（そのまま数秒お待ちください）</p>
      </div>
    );
  }

  // ★ 追加: キャンセル完了後の表示
  if (cancelFinished) {
    return (
      <div style={containerStyle}>
        <h2 style={{...errorTitleStyle, color: '#333'}}>キャンセル完了</h2>
        <p style={errorTextStyle}>
          進行中だった案件のキャンセルが完了しました。<br />
          LINEのメニューから、新しくお申し込みください。
        </p>
        <div className="text-muted small">×ボタンで画面を閉じてください</div>
      </div>
    );
  }

  // エラー画面の表示
  if (error) {
    return (
      <div style={containerStyle}>
        {title && <h2 style={errorTitleStyle}>{title}</h2>}
        <p style={errorTextStyle}>{error}</p>
        
        {/* ★ 追加: エラー文に「現在進行中」が含まれていて、かつUUIDがある場合のみボタンを表示！ */}
        {error.includes("現在進行中") && uuid && (
          <button style={cancelBtnStyle} onClick={handleCancel}>
            進行中の案件をキャンセルする
          </button>
        )}

        <div style={contactStyle}>
          ※ご不明な点がある場合は、<br />
          公式LINEよりお問い合わせください。
        </div>
      </div>
    );
  }

  return null;
}

export default FormGatekeeper;
